import {ErrorMessage} from 'formik'
import React, {useState, useEffect} from 'react'
import MediaManagerModal from '../MediaManagerModal'
import {isEmpty} from 'lodash'
import {FaPlus} from 'react-icons/fa'

type Props = {
  containerClassName?: string
  label?: string
  name: string
  altTextName?: string
  initialValue?: any
  rules?: any
  labelClassName?: string
  required?: boolean
  className?: string
  setFieldValue: any
  value: any
  altTextValue?: string
  setImageUrl?: any
  note?: string
  readOnly?: boolean
  showImageOnly?: boolean
  buttonName?: string
}

const FormInputMediaManager: React.FC<Props> = ({
  containerClassName,
  labelClassName,
  label,
  name,
  altTextName,
  initialValue: _initialValue,
  rules: _rules,
  required,
  className,
  setFieldValue,
  value,
  altTextValue = '',
  setImageUrl,
  note,
  readOnly,
  showImageOnly,
  buttonName,
}) => {
  const [mediaManagerModalOpen, setMediaManagerModalOpen] = useState(false)
  const [isDecorative, setIsDecorative] = useState(false)

  // Reset decorative state when a new image is selected
  useEffect(() => {
    setIsDecorative(false)
  }, [value])

  const handleCloseMediaManagerModal = () => setMediaManagerModalOpen(false)

  const callBackFile = {
    callback: (downloadLink: string, _title: string, storageLink: string) => {
      setFieldValue(name, storageLink)
      setImageUrl && setImageUrl(downloadLink)
    },
  }

  const handleDecorativeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    setIsDecorative(checked)
    if (altTextName) {
      setFieldValue(altTextName, checked ? '' : altTextValue)
    }
  }

  const handleAltTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (altTextName) setFieldValue(altTextName, e.target.value)
  }

  const altFieldId = altTextName ? `${altTextName}-input` : undefined
  const decorativeId = altTextName ? `${altTextName}-decorative` : undefined

  return (
    <div className={label ? 'mb-6' : 'col-lg-6 '}>
      {label && (
        <label className={`mb-1 fw-bolder fs-6 ${labelClassName || 'col-lg-4'}`}>
          <span className={required ? 'required' : ''}>{label}</span>
        </label>
      )}
      <div className={containerClassName ?? 'col-lg-6'}>
        <div className='d-flex flex-column'>
          {isEmpty(value) ? (
            <button
              type='button'
              className='dt-btn dt-btn-primary'
              onClick={() => setMediaManagerModalOpen(true)}
              data-cy={`${name}-media-manager-field`}
            >
              <FaPlus className='me-2' />
              {buttonName ?? 'Add New Image'}
            </button>
          ) : (
            <input
              name={name}
              type='text'
              className={className || 'form-control border mt-2'}
              autoComplete='off'
              value={value}
              readOnly={readOnly}
              data-cy={`${name}-media-manager-field`}
              onClick={() => setMediaManagerModalOpen(true)}
              onChange={() => {}}
            />
          )}

          {!isEmpty(value) && altTextName && (
            <div className='mt-3'>
              <label
                htmlFor={altFieldId}
                className='fw-bolder fs-7 mb-1 d-block'
              >
                <span className='required'>Alt text</span>
                <small className='fw-normal text-muted ms-2'>
                  (describe the image for screen reader users)
                </small>
              </label>

              {!isDecorative && (
                <>
                  <input
                    id={altFieldId}
                    type='text'
                    className='form-control border mt-1'
                    value={altTextValue}
                    onChange={handleAltTextChange}
                    placeholder='e.g. "Branch manager speaking at the annual event"'
                    maxLength={150}
                    aria-required='true'
                    aria-describedby={`${altTextName}-error`}
                    data-cy={`${altTextName}-alt-text-field`}
                  />
                  <small className='text-muted d-block text-end mt-1'>
                    {(altTextValue ?? '').length}/150
                  </small>
                </>
              )}

              <div className='form-check mt-2'>
                <input
                  id={decorativeId}
                  type='checkbox'
                  className='form-check-input'
                  checked={isDecorative}
                  onChange={handleDecorativeChange}
                  data-cy={`${altTextName}-decorative-checkbox`}
                />
                <label className='form-check-label fs-7' htmlFor={decorativeId}>
                  This image is decorative (logo, divider, icon) — no description needed
                </label>
              </div>

              <div
                id={`${altTextName}-error`}
                className='fv-plugins-message-container text-danger fw-bolder small text-end mt-1'
              >
                <ErrorMessage name={altTextName} component='div' className='field-error-message' />
              </div>
            </div>
          )}

          {!isEmpty(note) && (
            <em>
              <small>
                <span className='text-danger'>Note:</span> {note}
              </small>
            </em>
          )}
        </div>
        <div className='fv-plugins-message-container text-danger fw-bolder small text-end mt-1'>
          <ErrorMessage name={name} component='div' className='field-error-message' />
        </div>
      </div>
      <MediaManagerModal
        isOpen={mediaManagerModalOpen}
        handleClose={handleCloseMediaManagerModal}
        editorCallBack={callBackFile}
        showImageOnly={showImageOnly}
      />
    </div>
  )
}

export default FormInputMediaManager
